import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ChevronRight, Send, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { lmsGetMyHomework, lmsSubmitHomework, uploadHomeworkAttachment, type LmsHomeworkAssignment } from '@/lib/api';

type StatusKey = LmsHomeworkAssignment['status'];

const STATUS_CONFIG: Record<StatusKey, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-africa-blue/10 text-africa-blue border-africa-blue/20' },
  submitted: { label: 'Submitted', className: 'bg-africa-orange/10 text-africa-orange border-africa-orange/20' },
  graded: { label: 'Graded', className: 'bg-africa-green/10 text-africa-green border-africa-green/20' },
  late: { label: 'Overdue', className: 'bg-destructive/10 text-destructive border-destructive/20' },
};

export function HomeworkPage() {
  const [assignments, setAssignments] = useState<LmsHomeworkAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<LmsHomeworkAssignment | null>(null);
  const [content, setContent] = useState('');
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setLoading(true);
    lmsGetMyHomework()
      .then((res) => setAssignments(res.assignments ?? []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load homework'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (!selected || !content.trim()) return;
    setSubmitting(true);
    try {
      await lmsSubmitHomework(selected.id, { content, fileNames });
      const res = await lmsGetMyHomework();
      setAssignments(res.assignments ?? []);
      setSelected(null);
      setContent('');
      setFileNames([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit homework');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = {
    total: assignments.length,
    pending: assignments.filter((a) => a.status === 'pending').length,
    submitted: assignments.filter((a) => a.status === 'submitted').length,
    graded: assignments.filter((a) => a.status === 'graded').length,
    overdue: assignments.filter((a) => a.status === 'late').length,
  };

  const getDaysLeft = (dueDate: string | null) =>
    dueDate != null
      ? Math.ceil((new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="h-8 w-48 bg-muted rounded animate-pulse mb-6" />
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 rounded-xl border border-border bg-card animate-pulse" />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-36 rounded-xl border border-border bg-card animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="bg-primary py-6 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck className="w-6 h-6 text-accent" />
            <h1 className="text-xl md:text-2xl font-display font-bold text-primary-foreground">My homework</h1>
          </div>
          <p className="text-primary-foreground/60 text-sm">
            View assignments, submit work, and track your grades.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="text-xs text-muted-foreground">
          <Link to="/dashboard" className="hover:underline">
            ← Back to dashboard
          </Link>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}

        {!selected ? (
          <div className="space-y-5">
            {assignments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No homework assigned yet.</p>
              </div>
            ) : (
              <>
                {/* Stats row */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { label: 'Total', value: stats.total, icon: ClipboardCheck, className: 'text-foreground' },
                    { label: 'Pending', value: stats.pending, icon: Clock, className: 'text-africa-blue' },
                    { label: 'Submitted', value: stats.submitted, icon: Send, className: 'text-africa-orange' },
                    { label: 'Graded', value: stats.graded, icon: CheckCircle2, className: 'text-africa-green' },
                    { label: 'Overdue', value: stats.overdue, icon: AlertCircle, className: 'text-destructive' },
                  ].map(({ label, value, icon: Icon, className }) => (
                    <div
                      key={label}
                      className="rounded-xl border border-border bg-card px-3 py-3 flex items-center gap-2"
                    >
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <Icon className={`w-4 h-4 ${className}`} />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-lg font-display font-bold tabular-nums ${className}`}>{value}</p>
                        <p className="text-[10px] text-muted-foreground">{label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Homework cards grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {assignments.map((a) => {
                    const statusConfig = STATUS_CONFIG[a.status];
                    const daysLeft = getDaysLeft(a.dueDate);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setSelected(a)}
                        className="text-left rounded-xl border border-border bg-card p-4 flex flex-col gap-2 hover:border-accent/40 hover:bg-secondary/30 transition-all shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                            <ClipboardCheck className="w-4 h-4 text-accent" />
                          </div>
                          <span
                            className={`text-[10px] font-medium px-2 py-0.5 rounded-full border shrink-0 ${statusConfig.className}`}
                          >
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-foreground line-clamp-2">{a.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.courseTitle}</p>
                        <div className="flex items-center justify-between mt-auto pt-1">
                          {a.dueDate && (
                            <span
                              className={`text-[10px] ${
                                daysLeft != null && daysLeft < 0
                                  ? 'text-destructive'
                                  : daysLeft != null && daysLeft <= 2
                                    ? 'text-africa-orange'
                                    : 'text-muted-foreground'
                              }`}
                            >
                              {daysLeft != null
                                ? daysLeft < 0
                                  ? `${Math.abs(daysLeft)}d overdue`
                                  : daysLeft === 0
                                    ? 'Due today'
                                    : `${daysLeft}d left`
                                : `Due ${a.dueDate}`}
                            </span>
                          )}
                          {a.status === 'graded' && a.score != null && (
                            <span className="text-[10px] font-medium text-africa-green">{a.score} pts</span>
                          )}
                          <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="gap-1">
              <ChevronRight className="-rotate-180 w-4 h-4" /> Back to list
            </Button>
            <div className="border border-border rounded-xl bg-card p-4 space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Assignment</p>
                <h2 className="text-lg font-display font-bold text-foreground">{selected.title}</h2>
                <p className="text-xs text-muted-foreground">
                  {selected.courseTitle}
                  {selected.dueDate && <> · Due {selected.dueDate}</>}
                </p>
              </div>
              {selected.instructions && (
                <div>
                  <p className="text-xs font-semibold text-foreground mb-1">Instructions</p>
                  <p className="text-sm text-foreground whitespace-pre-line bg-secondary/40 rounded-lg p-3">
                    {selected.instructions}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-foreground mb-1">Your response</p>
                <Textarea
                  rows={6}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your homework response here..."
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground mb-1">Attach files (optional)</p>
                <p className="text-[10px] text-muted-foreground mb-1">
                  Upload your homework files (images, PDFs, or Word documents), then click submit.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files ?? []);
                    if (!files.length) return;
                    setUploading(true);
                    try {
                      const uploads: string[] = [];
                      for (const file of files) {
                        // Upload each attachment and collect the public URLs
                        const { url } = await uploadHomeworkAttachment(file);
                        uploads.push(url);
                      }
                      setFileNames((prev) => [...prev, ...uploads]);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to upload attachment');
                    } finally {
                      setUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
                <div className="flex gap-2 mb-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gap-1"
                  >
                    <Send className="w-3 h-3" /> {uploading ? 'Uploading…' : 'Choose files'}
                  </Button>
                </div>
                {fileNames.length > 0 && (
                  <div className="flex flex-wrap gap-1 text-[10px] text-muted-foreground">
                    {fileNames.map((f, i) => {
                      const label = f.split('/').pop() ?? f;
                      return (
                        <span
                          key={i}
                          className="px-2 py-0.5 rounded-full bg-secondary cursor-pointer"
                          onClick={() => setFileNames((prev) => prev.filter((_, j) => j !== i))}
                        >
                          {label} ×
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelected(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!content.trim() || submitting || uploading}
                  className="gap-1"
                >
                  <Send className="w-4 h-4" /> {submitting ? 'Submitting…' : 'Submit'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

